/// <reference path="./clipper-lib.d.ts" />
import ClipperLib from 'clipper-lib';
import { EntryEditError } from '../edits.ts';
import { javaFixed, javaRound } from '../javaText.ts';
import { int, scalar, transaction, type SqlConnection } from '../sql.ts';
import { decodePoints, encodePoints, type MapPoint } from './geometry.ts';

/**
 * Fog of war (docs/PARITY-STEP-4-PLAN.md section 8), as the desktop's `MapFog`: when it's on,
 * the whole map starts hidden from players and the GM reveals (or covers again) parts of it
 * with a round brush, a box or a region's outline. What's kept is the revealed area: rings of
 * "x,y;..." (map pixels, to a tenth) joined by "|", read even-odd so holes stay holes.
 *
 * The desktop changes the area with Java's geometry library; the web uses clipper-lib
 * (decision 3), in whole hundredths of a pixel, so repeated strokes can't drift. Its rings
 * aren't the desktop's point for point, but cover the same area within a pixel, which
 * `schema/fixtures/fog-cases.json` checks; and each edition reads the other's.
 */

/** Clipper works in integers: hundredths of a map pixel. */
const SCALE = 100;
/** How far a curve (the brush's round ends) may stray from its flattened outline, in map pixels (`MapFog.FLATNESS`). */
const FLATNESS = 0.75;
export const FOG_LIMITS = { stored: 2_000_000, brush: 2_000, points: 20_000 } as const;

type IntPath = { X: number; Y: number }[];
export type Rings = readonly (readonly MapPoint[])[];

export function parseFog(stored: string): MapPoint[][] {
  if (stored.trim() === '') return [];
  return stored.split('|').map(decodePoints).filter((ring) => ring.length >= 3);
}

/** `MapFog.encode`: each ring's points rounded to a tenth, written as the desktop writes them. */
export function encodeFog(rings: Rings): string {
  return rings.map((ring) => encodePoints(ring)).join('|');
}

const toInt = (rings: Rings): IntPath[] => rings.map((ring) => ring.map((p) => ({ X: Math.round(p.x * SCALE), Y: Math.round(p.y * SCALE) })));
const tenth = (v: number) => javaRound(v * 10) / 10;

/** Clipper's result as rings, to a tenth of a pixel, without the rings that rounding leaves with no area. */
function fromInt(paths: IntPath[]): MapPoint[][] {
  const rings: MapPoint[][] = [];
  for (const path of paths) {
    const ring: MapPoint[] = [];
    for (const pt of path) {
      const p = { x: tenth(pt.X / SCALE), y: tenth(pt.Y / SCALE) };
      const last = ring.at(-1);
      if (!last || last.x !== p.x || last.y !== p.y) ring.push(p);
    }
    while (ring.length > 1 && ring[0]!.x === ring.at(-1)!.x && ring[0]!.y === ring.at(-1)!.y) ring.pop();
    if (ring.length >= 3 && Math.abs(ringArea(ring)) > 1e-9) rings.push(ring);
  }
  return rings;
}

function ringArea(ring: readonly MapPoint[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

function clip(op: number, subject: IntPath[], clipPaths: IntPath[]): IntPath[] {
  const c = new ClipperLib.Clipper();
  c.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
  c.AddPaths(clipPaths, ClipperLib.PolyType.ptClip, true);
  const out: IntPath[] = [];
  // What's stored is read even-odd; a painted shape is filled whole.
  c.Execute(op, out, ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftNonZero);
  return out;
}

// ---- the shapes the GM paints with ----

/** A round brush dragged along the points (one point is one dab), as Java's round-capped `BasicStroke`. */
export function brushShape(points: readonly MapPoint[], radius: number): Rings {
  if (points.length === 0 || !(radius > 0)) return [];
  const offset = new ClipperLib.ClipperOffset(2, FLATNESS * SCALE);
  offset.AddPaths(toInt([points]), ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
  const out: IntPath[] = [];
  offset.Execute(out, radius * SCALE);
  return fromInt(out);
}

/** The box between two opposite corners. */
export function boxShape(a: MapPoint, b: MapPoint): Rings {
  const [x0, x1] = [Math.min(a.x, b.x), Math.max(a.x, b.x)];
  const [y0, y1] = [Math.min(a.y, b.y), Math.max(a.y, b.y)];
  return [[{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }]];
}

/** A closed polygon: a region's outline. */
export const polygonShape = (points: readonly MapPoint[]): Rings => (points.length >= 3 ? [points] : []);

// ---- changing it ----

/** Clears the fog from a shape (`MapFog.reveal`). */
export function revealFog(revealed: Rings, shape: Rings): MapPoint[][] {
  return fromInt(clip(ClipperLib.ClipType.ctUnion, toInt(revealed), toInt(shape)));
}

/** Covers a shape with fog again (`MapFog.hide`). */
export function hideFog(revealed: Rings, shape: Rings): MapPoint[][] {
  return fromInt(clip(ClipperLib.ClipType.ctDifference, toInt(revealed), toInt(shape)));
}

/** The whole map revealed, fog still on, so later hides work (`revealAll`). */
export const revealAllFog = (width: number, height: number): MapPoint[][] => [
  [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ],
];

// ---- reading it ----

/** Whether players can see this point: always with fog off, else if it's in the revealed area (even-odd). */
export function isRevealed(enabled: boolean, revealed: Rings, p: MapPoint): boolean {
  if (!enabled) return true;
  let inside = false;
  for (const ring of revealed) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i]!;
      const b = ring[j]!;
      if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
    }
  }
  return inside;
}

/** Whether any part of the polygon is revealed (always with fog off): a region players can see some of. */
export function revealsAny(enabled: boolean, revealed: Rings, polygon: readonly MapPoint[]): boolean {
  if (!enabled) return true;
  if (polygon.length < 3) return polygon.some((p) => isRevealed(enabled, revealed, p));
  return fromInt(clip(ClipperLib.ClipType.ctIntersection, toInt(revealed), toInt([polygon]))).length > 0;
}

/** How much of the map is revealed, 0 to 1 (`revealedFraction`). */
export function revealedFraction(enabled: boolean, revealed: Rings, width: number, height: number): number {
  if (!enabled) return 1;
  if (width <= 0 || height <= 0) return 0;
  const inside = clip(ClipperLib.ClipType.ctIntersection, toInt(revealed), toInt(revealAllFog(width, height)));
  const area = inside.reduce((sum, path) => sum + ClipperLib.Clipper.Area(path), 0) / (SCALE * SCALE);
  return Math.max(0, Math.min(1, Math.abs(area) / (width * height)));
}

/** `MapFog.num`: whole numbers whole, else one place. */
const num = (v: number) => (Number.isInteger(v) ? String(v) : javaFixed(v, 1));

/**
 * The fogged part of the map as SVG path data, filled even-odd (`fogPathData`), carried
 * `margin` pixels past the map's edges; empty with fog off.
 */
export function fogPath(enabled: boolean, revealed: Rings, width: number, height: number, margin = 0): string {
  if (!enabled) return '';
  const all = [[{ x: -margin, y: -margin }, { x: width + margin, y: -margin }, { x: width + margin, y: height + margin }, { x: -margin, y: height + margin }]];
  return fromInt(clip(ClipperLib.ClipType.ctDifference, toInt(all), toInt(revealed)))
    .map((ring) => `M${num(ring[0]!.x)},${num(ring[0]!.y)}${ring.slice(1).map((p) => `L${num(p.x)},${num(p.y)}`).join('')}Z`)
    .join('');
}

// ---- saving ----

/** Saves a map's fog without touching anything else (`AtlasRepository.updateFog`). */
export function updateFog(conn: SqlConnection, mapId: string, enabled: boolean, revealed: string, now: string = new Date().toISOString()): void {
  if (revealed.length > FOG_LIMITS.stored) throw new EntryEditError('invalid', "That much fog can't be kept.");
  // Kept as the desktop would read it: rings of at least three points.
  const clean = encodeFog(parseFog(revealed));
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM atlas_maps WHERE id = ?', [mapId])) === 0) throw new EntryEditError('not_found', 'That map no longer exists.');
    conn.run('UPDATE atlas_maps SET fog_enabled = ?, fog_revealed = ?, modified = ? WHERE id = ?', [enabled ? 1 : 0, clean, now, mapId]);
  });
}
